import { forwardRef, useState } from 'react';

import { RowWrapper } from 'modules/workflowNodeEditors/EditorBody.styled';
import { ALIGNMENT } from 'ui/menu';
import { ChevronDown } from 'ui/icons';
import { BasicSelect } from 'ui/selects';

import { DropdownContent } from './DropdownContent';
import {
  SelectiveTargetingOptionValues,
  SELECTIVE_TARGETING_OPTIONS,
  SELECTIVE_TARGETING_PLACEHOLDER_OPTIONS,
} from './SelectiveTargeting.constants';
import {
  AppsCounter,
  AppsListPreview,
  AppsListSubtextContainer,
  DropdownButton,
  SelectiveTargetingSection,
  StyledDropdownContentContainer,
  TriggerPreview,
} from './SelectiveTargeting.styled';
import {
  SelectiveTargetingBaseProps,
  SelectiveTargetingDropdownTriggerProps,
  SelectiveTargetingOption,
} from './SelectiveTargeting.types';
import {
  combineSubtextVisibleListWithOverflow,
  getAppsForSelectedOption,
  getAppsListSubtext,
} from './utils';

/**
 *
 * This component is currently a direct copy of <SelectivePush />,
 * which is the more limited of the destination options. However,
 * it may evolve over time to include some, but not all, of the
 * options available in <SelectiveInApp />. Due to the likelihood of
 * in-flux requirements for embedded that may differ from either
 * existing medium, a separate component that can be updated
 * independently seems like the best way to go for now.
 *
 */
export const SelectiveEmbedded = ({
  mobileApps = [],
  mobileAppIds,
  setTargetingType,
  setMobileAppIds,
  setIsSelectiveTargetingEnabled,
  customEnabled = true,
}: SelectiveTargetingBaseProps & {
  setTargetingType: (type: SelectiveTargetingOptionValues) => void;
  customEnabled?: boolean;
}) => {
  const targetingOptions = SELECTIVE_TARGETING_OPTIONS.filter((option) =>
    [
      SelectiveTargetingOptionValues.EVERYTHING,
      SelectiveTargetingOptionValues.ALL_MOBILE_APPS,
      SelectiveTargetingOptionValues.ALL_WEBSITES,
      ...(customEnabled ? SelectiveTargetingOptionValues.CUSTOM : []),
    ].includes(option.value),
  );
  const getTargetingOptionFromValue = (value: SelectiveTargetingOptionValues) =>
    targetingOptions.find((option) => option.value === value) || targetingOptions[0];
  const [selectedTargetingOption, setSelectedTargetingOption] = useState(
    getTargetingOptionFromValue(
      mobileAppIds.length === 0
        ? SelectiveTargetingOptionValues.ALL_MOBILE_APPS
        : SelectiveTargetingOptionValues.CUSTOM,
    ),
  );

  const onChangeSelectedTargetingOption = (option: SelectiveTargetingOption) => {
    setTargetingType(option.value);
    setMobileAppIds([]);
    setSelectedTargetingOption(option);
    setIsSelectiveTargetingEnabled(option.value !== SelectiveTargetingOptionValues.EVERYTHING);
  };

  const getDropdownContent = () => (
    <StyledDropdownContentContainer data-test="selective-embedded-dropdown">
      <DropdownContent
        selectedTargetingOption={selectedTargetingOption}
        onChangeSelectedTargetingOption={onChangeSelectedTargetingOption}
        targetingOptions={targetingOptions}
        mobileApps={mobileApps}
        mobileAppIds={mobileAppIds}
        setMobileAppIds={setMobileAppIds}
      />
    </StyledDropdownContentContainer>
  );

  const Trigger = forwardRef<HTMLButtonElement, SelectiveTargetingDropdownTriggerProps>(
    ({ toggleDropdown }, ref) => (
      <DropdownButton ref={ref} onClick={toggleDropdown}>
        <TriggerPreview>
          {selectedTargetingOption.name}
          <AppsCounter>
            {selectedTargetingOption.value === SelectiveTargetingOptionValues.CUSTOM
              ? mobileAppIds.length
              : getAppsForSelectedOption(selectedTargetingOption, mobileApps).length}
          </AppsCounter>
        </TriggerPreview>
        <ChevronDown height={16} width={16} />
      </DropdownButton>
    ),
  );

  return (
    <RowWrapper>
      <SelectiveTargetingSection>
        <BasicSelect
          id="embedded-destination-select"
          className="destination-select"
          Option={getDropdownContent}
          options={SELECTIVE_TARGETING_PLACEHOLDER_OPTIONS}
          style={{ display: 'block' }}
          Trigger={Trigger}
          verticalAlignment={ALIGNMENT.UP}
        />
        <AppsListPreview>
          <AppsListSubtextContainer>
            {selectedTargetingOption.value === SelectiveTargetingOptionValues.CUSTOM
              ? combineSubtextVisibleListWithOverflow(
                  mobileApps.filter((app) => mobileAppIds.find((id) => app.id === id)),
                )
              : getAppsListSubtext(selectedTargetingOption, mobileApps)}
          </AppsListSubtextContainer>
        </AppsListPreview>
      </SelectiveTargetingSection>
    </RowWrapper>
  );
};
